import { FC } from 'react';
import { IllustEmptyProduct, IllustEmptyWarning, MiniLoader } from '@croquiscom-pvt/zds';
import { vars } from '@croquiscom-pvt/zds/dist/tokens';
import styled from '@emotion/styled';

import { Log } from '@common/log-manager';
import {
  BestProductCarouselComponentType,
  ChipAndSortingComponentType,
  ChipAndSortingFilter,
  ComponentItem,
  EmptyViewError,
  FilterParam,
  FullLookBookComponentType,
  GrouppedComponentListMap,
  ImageBannerListComponentType,
  ImageCardComponentType,
  LookBookComponentType,
  ProductGroupComponentType,
  ShopUxComponentType,
  TextAndSortingComponentType,
  TextAndSortingFilter,
} from '@domains/storehome-common';

import { BestProductCarouselContainer } from '../best-product-carousel/containers/BestProductCarouselContainer';
import { ImageBannerListContainer } from '../collection/image-banner-list/containers/ImageBannerListContainer';
import { ImageCardContainer } from '../collection/image-card/containers/ImageCardContainer';
import { FullLookbookContainer } from '../collection/lookbook/containers/FullLookbookContainer';
import { LookbookContainer } from '../collection/lookbook/containers/LookbookContainer';
import { ProductGroupContainer } from '../product-group/containers/ProductGroupContainer';
import { UxProductList } from '../product-list';
import { useComponentListService } from '../services/useComponentListService';

const getUBLObjectSectionByContentType = (type: string) => {
  switch (type) {
    case 'BEST_PRODUCTS':
      return 'best';
    case 'ZIGZIN_PRODUCTS':
      return 'zonly';
    case 'COLLECTION':
      return 'shop_collection_display';
    default:
      return 'all';
  }
};

interface Props {
  type: 'store' | 'category' | 'zigzin-shipping';
  params: {
    shop_id: string;
    category_id?: string | null;
    check_button_item_ids?: string[];
    sub_filter_id_list?: string[];
  };
  components: GrouppedComponentListMap;
  component_list: Array<ComponentItem | null>;
  is_fetching: boolean;
  error?: unknown;
  has_next_page?: boolean;
  is_selected?: boolean;
  onFetchNextPage: () => void;
  onParamsChange: (params: FilterParam) => void;
  refetch?: () => void;
}

export const StorehomeMainComponentList: FC<React.PropsWithChildren<Props>> = ({
  type,
  params,
  components,
  component_list,
  is_fetching,
  error,
  has_next_page,
  onFetchNextPage,
  onParamsChange,
  refetch,
}) => {
  const { pageLog, columnCount, setColumnCount, targetRef } = useComponentListService({
    type,
    params,
    has_next_page,
    onFetchNextPage,
  });

  if (error) {
    return <EmptyViewError size='small' retry={refetch} />;
  }

  const {
    PRODUCT_GROUP: product_group_data,
    TEXT_AND_SORT: text_and_sort_data,
    CHIP_AND_SORT: chip_and_sort_data,
    PRODUCT: product_data,
  } = components;

  const isEmpty =
    !product_group_data?.length &&
    !product_data?.length &&
    params.check_button_item_ids?.length === 0 &&
    params.sub_filter_id_list?.length === 0;

  if (!is_fetching && isEmpty) {
    return (
      <ErrorWrap>
        <EmptyViewError
          icon={<IllustEmptyWarning />}
          title='상품이 없어요'
          description={`스토어에서 상품을 준비 중이에요.\n조금만 기다려 주세요!`}
          size='small'
        />
      </ErrorWrap>
    );
  }

  return (
    <ComponentListWrap className='component-list'>
      <div className='default-component-list'>
        {component_list
          .filter((component) => {
            return (
              component !== null &&
              component.type !== ShopUxComponentType.TEXT_AND_SORT &&
              component.type !== ShopUxComponentType.PRODUCT
            );
          })
          .map((component, index) => renderComponentByType(component, index, params, pageLog))}
      </div>

      {text_and_sort_data && (
        <TextAndSortingFilter
          active_navigation_id={params.category_id}
          component={text_and_sort_data[0] as TextAndSortingComponentType}
          onChangeFilter={onParamsChange}
          log={{
            navigation: params.category_id ? 'shop_category_detail' : 'shop_detail',
            navigation_sub: { shop_id: params.shop_id },
            ...(pageLog && pageLog),
          }}
        />
      )}

      {chip_and_sort_data && (
        <ChipAndSortingFilter
          component={chip_and_sort_data[0] as ChipAndSortingComponentType}
          onChangeFilter={onParamsChange}
          columnCount={columnCount}
          setColumnCount={setColumnCount}
          shopId={params.shop_id}
          log={{
            navigation: params.category_id ? 'shop_category_detail' : 'shop_detail',
            navigation_sub: { shop_id: params.shop_id },
            ...(pageLog && pageLog),
          }}
        />
      )}

      {product_data && product_data.length ? (
        <UxProductList
          component={product_data}
          log={
            pageLog && {
              ...pageLog,
              object_section: 'all',
            }
          }
          use_impression
          columnCount={columnCount}
        />
      ) : (
        !is_fetching && (
          <ErrorWrap>
            <EmptyViewError
              icon={<IllustEmptyProduct />}
              title='조건에 맞는 상품이 없어요'
              description='필터를 수정해 주세요.'
              size='small'
            />
          </ErrorWrap>
        )
      )}

      {has_next_page && (
        <LoadingWrap ref={targetRef}>
          <MiniLoader />
        </LoadingWrap>
      )}
    </ComponentListWrap>
  );
};

const renderComponentByType = (
  component: ComponentItem | null,
  index: number,
  params: { shop_id: string; category_id?: string | null },
  page_log: Partial<Log> | undefined,
) => {
  if (!component) return null;
  const globalIndex = index;

  switch (component.type) {
    case ShopUxComponentType.PRODUCT_GROUP: {
      const productGroupComponent = component as ProductGroupComponentType;
      return (
        <ProductGroupContainer
          key={`${params.shop_id}_${params.category_id}_${component.type}_${globalIndex}`}
          shop_id={params.shop_id}
          category_id={params.category_id || ''}
          component={productGroupComponent}
          log={
            page_log && {
              ...page_log,
              object_section: getUBLObjectSectionByContentType(productGroupComponent.contents_type),
            }
          }
        />
      );
    }

    case ShopUxComponentType.IMAGE_BANNER_AND_LIST: {
      const imageBannerComponent = component as ImageBannerListComponentType;
      return (
        <ImageBannerListContainer
          key={`${params.shop_id}_${component.type}_${globalIndex}`}
          component={imageBannerComponent}
          log={
            page_log && {
              ...page_log,
              object_section: 'shop_collection_display',
            }
          }
        />
      );
    }

    case ShopUxComponentType.IMAGE_CARD: {
      const imageCardComponent = component as ImageCardComponentType;
      return (
        <ImageCardContainer
          key={`${params.shop_id}_${component.type}_${globalIndex}`}
          component={imageCardComponent}
          log={
            page_log && {
              ...page_log,
              object_section: imageCardComponent.ubl?.object?.section ?? 'shop_collection_display',
              object_id: imageCardComponent.ubl?.object?.id ?? 'shop_collection_display_image',
              data: {
                server_log: {
                  ...(imageCardComponent.ubl?.server_log && JSON.parse(imageCardComponent.ubl.server_log)),
                  component_idx: globalIndex,
                },
              },
            }
          }
        />
      );
    }

    case ShopUxComponentType.LOOKBOOK: {
      const lookbookComponent = component as LookBookComponentType;
      return (
        <LookbookContainer key={`${params.shop_id}_${component.type}_${globalIndex}`} component={lookbookComponent} />
      );
    }

    case ShopUxComponentType.FULL_LOOKBOOK: {
      const fullLookbookComponent = component as FullLookBookComponentType;
      return (
        <FullLookbookContainer
          key={`${params.shop_id}_${component.type}_${globalIndex}`}
          component={fullLookbookComponent}
          log={
            page_log && {
              ...page_log,
              object_section: fullLookbookComponent.ubl?.object?.section ?? 'shop_collection_display',
              object_id: fullLookbookComponent.ubl?.object?.id ?? 'shop_collection_display_image',
              data: {
                server_log: {
                  ...(fullLookbookComponent.ubl?.server_log && JSON.parse(fullLookbookComponent.ubl.server_log)),
                  component_idx: globalIndex,
                },
              },
            }
          }
        />
      );
    }

    case ShopUxComponentType.BEST_PRODUCT_CAROUSEL: {
      const bestProductCarouselComponent = component as BestProductCarouselComponentType;
      return (
        <BestProductCarouselContainer
          key={`${params.shop_id}_${component.type}_${globalIndex}`}
          shop_id={params.shop_id}
          category_id={params.category_id || ''}
          component={bestProductCarouselComponent}
          log={
            page_log && {
              ...page_log,
              object_section: 'best',
            }
          }
        />
      );
    }

    default:
      return null;
  }
};

const ComponentListWrap = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  background-color: ${vars.color.staticWhite};
  z-index: 10;

  .default-component-list {
    display: flex;
    flex-direction: column;
    gap: 24px;
    &:not(:empty) {
      padding-bottom: 24px;
    }
  }
`;

const LoadingWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 155px;
`;

const ErrorWrap = styled.div`
  position: relative;
  flex: 1;
  height: 1px;
  min-height: 400px;
  background-color: ${vars.color.staticWhite};
`;
